import { useSyncExternalStore } from "react";
import type { CountryLite } from "@shared/types";

// The user's picked "home" nation, persisted per-browser in localStorage (the
// app is anonymous — no accounts). Backed by a module-level external store so
// every consumer shares one value: the persistent <Nav> chip, the switcher, and
// any page that reads it all stay in sync without a reload. The `storage` event
// also keeps multiple tabs consistent.

const KEY = "warurna:nation";

function read(): CountryLite | null {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as CountryLite) : null;
  } catch {
    return null;
  }
}

let current: CountryLite | null = read();
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

// One cross-tab listener for the whole store (not one per subscriber): when
// another tab changes the pick, refresh and notify every consumer.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e: StorageEvent) => {
    if (e.key === KEY) {
      current = read();
      emit();
    }
  });
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

export function setNation(c: CountryLite): void {
  current = c;
  try {
    localStorage.setItem(KEY, JSON.stringify(c));
  } catch {
    // storage unavailable (private mode) — the pick just won't persist
  }
  emit();
}

export function clearNation(): void {
  current = null;
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
  emit();
}

/** Subscribe to the picked nation. Returns `{ nation, setNation, clear }`. */
export function useNation() {
  const nation = useSyncExternalStore(subscribe, () => current, () => current);
  return { nation, setNation, clear: clearNation };
}
